import { useEffect, useState } from 'react';
import { LayoutList, Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMenuManager() {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label_hr: '', url: '', order: 0, visible: true, parent_id: '' });

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/menu`);
    const d = await r.json();
    setItems(d.items || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.label_hr || !form.url) return toast.error('Oznaka i URL su obavezni');
    const r = await authFetch(`${API}/admin/menu`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, parent_id: form.parent_id || null }),
    });
    if (r.ok) { toast.success('Dodano'); setForm({ label_hr: '', url: '', order: items.length, visible: true, parent_id: '' }); load(); } else toast.error('Greška');
  };

  const update = async (it, patch) => {
    const r = await authFetch(`${API}/admin/menu/${it.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (r.ok) load(); else toast.error('Greška');
  };

  const del = async (it) => {
    if (!window.confirm(`Izbrisati "${it.label_hr}"?`)) return;
    const r = await authFetch(`${API}/admin/menu/${it.id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Izbrisano'); load(); }
  };

  const move = async (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const newOrder = [...items];
    [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
    const payload = newOrder.map((it, i) => ({ id: it.id, order: i }));
    await authFetch(`${API}/admin/menu/reorder`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    load();
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-menu-manager">
      <div className="flex items-center gap-3 mb-6">
        <LayoutList className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Upravitelj izbornika</h1>
          <p className="text-sm text-zinc-400">Javni izbornik u headeru.</p>
        </div>
      </div>

      {/* Create form */}
      <div className="p-4 mb-6 rounded-lg border border-zinc-800 bg-zinc-900/50 grid md:grid-cols-4 gap-3">
        <input value={form.label_hr} onChange={e => setForm({ ...form, label_hr: e.target.value })}
               placeholder="Oznaka (HR)" data-testid="menu-new-label"
               className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
        <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
               placeholder="URL (npr. /kategorija/sport)" data-testid="menu-new-url"
               className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
        <select value={form.parent_id} onChange={e => setForm({ ...form, parent_id: e.target.value })}
                data-testid="menu-new-parent"
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm">
          <option value="">— Nema roditelja —</option>
          {items.filter(i => !i.parent_id).map(i => <option key={i.id} value={i.id}>{i.label_hr}</option>)}
        </select>
        <button onClick={create} data-testid="menu-new-btn"
                className="inline-flex items-center justify-center gap-1 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold"><Plus size={14}/> Dodaj</button>
      </div>

      {/* List */}
      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900 text-xs uppercase text-zinc-400">
              <tr><th className="px-3 py-2">Red.</th><th className="px-3 py-2 text-left">Oznaka</th><th className="px-3 py-2 text-left">URL</th><th className="px-3 py-2 text-left">Roditelj</th><th className="px-3 py-2">Vid.</th><th className="px-3 py-2">Akc.</th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id} data-testid={`menu-row-${it.id}`} className="border-t border-zinc-800">
                  <td className="px-3 py-1.5 text-center text-zinc-500">
                    <button onClick={() => move(i, -1)} className="p-0.5 hover:text-zinc-200"><ArrowUp size={12}/></button>
                    <button onClick={() => move(i, 1)} className="p-0.5 hover:text-zinc-200"><ArrowDown size={12}/></button>
                  </td>
                  <td className="px-3 py-1.5">
                    <input defaultValue={it.label_hr} onBlur={e => e.target.value !== it.label_hr && update(it, { label_hr: e.target.value })}
                           data-testid={`menu-label-${it.id}`}
                           className="bg-transparent border-b border-zinc-800 focus:border-red-500 outline-none text-zinc-100 w-full" />
                  </td>
                  <td className="px-3 py-1.5">
                    <input defaultValue={it.url} onBlur={e => e.target.value !== it.url && update(it, { url: e.target.value })}
                           data-testid={`menu-url-${it.id}`}
                           className="bg-transparent border-b border-zinc-800 focus:border-red-500 outline-none text-zinc-400 text-xs w-full font-mono" />
                  </td>
                  <td className="px-3 py-1.5 text-zinc-500 text-xs">
                    {items.find(p => p.id === it.parent_id)?.label_hr || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <button onClick={() => update(it, { visible: !it.visible })} data-testid={`menu-visible-${it.id}`}
                            className={`p-1 rounded ${it.visible ? 'text-emerald-400' : 'text-zinc-600'}`}>
                      {it.visible ? <Eye size={14}/> : <EyeOff size={14}/>}
                    </button>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <button onClick={() => del(it)} data-testid={`menu-delete-${it.id}`}
                            className="p-1 rounded text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-zinc-500">Prazan izbornik.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
